import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Tooltip, Typography, Pagination } from "@mui/material";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

import { entityGenderEnum, entityRoleEnum, entityVerificationStatusEnum } from "../../enums/entity.enum";
import { AdvancedFilters } from "../../types";
import { colors } from "../../theme/palette";
import MembersComingSoon from "./MembersComingSoon";
import useNetworkList from "../../api/queries/network/networkListingQueries";

import CompareModal from "../CompareModal";
import NetworkTabs from "./NetworkTabs";
import NetworkRoles from "./NetworkRoles";
import NetworkFilters from "./NetworkFilters";
import NetworkListing from "./NetworkListing";

const NetworkController = ({ isOwnNetworkTab, isEntity }: { isOwnNetworkTab?: boolean; isEntity?: boolean }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const paramTabValue = tab === "following" ? 1 : 0;
  const [tabValue, setTabValue] = useState<number>(paramTabValue);

  useEffect(() => {
    setTabValue(paramTabValue);
  }, [paramTabValue]);

  const [tooltipMessage, setTooltipMessage] = useState<string>(t("components.compare.compareWarning"));
  const [roleTab, setRoleTab] = useState<number>(1);
  const [roleEntitySelected, setRoleEntitySelected] = useState<entityRoleEnum>(entityRoleEnum.PLAYER);
  const [page, setPage] = useState<number>(0);
  const [nationality, setNationality] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [gender, setGender] = useState<entityGenderEnum | "">("");
  const [verificationStatus, setVerificationStatus] = useState<entityVerificationStatusEnum | "">("");
  const [networkFilters, setNetworkFilters] = useState<AdvancedFilters>({});
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(networkFilters);
  const [toolTipShowMsg, setToolTipShowMsg] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState<any>([]);

  const { pathname } = useLocation();
  const { entityId, userId } = useParams();
  const [userParamId, setUserParamId] = useState(userId);

  useEffect(() => {
    setUserParamId(userId);
  }, [pathname]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setLocation("");
    setNationality("");
    setGender("");
    setVerificationStatus("");
    setPage(value - 1);
    setCompareData([]);
  };

  const handleChangeTabValue = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    setNetworkFilters({});
    setLocation("");
    setNationality("");
    setGender("");
    setVerificationStatus("");
    setRoleEntitySelected(entityRoleEnum.PLAYER);
    setRoleTab(1);
    setCompareData([]);
  }, []);

  const handleChangeChildTabValue = (newValue: number) => {
    setRoleTab(newValue);
    setPage(0);
    setNetworkFilters({});
    setLocation("");
    setNationality("");
    setGender("");
    setVerificationStatus("");
    setCompareData([]);

    const selectedRole =
      newValue === 1
        ? entityRoleEnum.PLAYER
        : newValue === 2
        ? entityRoleEnum.SCOUT
        : newValue === 3
        ? entityRoleEnum.AGENT
        : newValue === 4
        ? entityRoleEnum.COACH
        : newValue === 5
        ? entityRoleEnum.OTHER
        : entityRoleEnum.PLAYER;

    setRoleEntitySelected(selectedRole);
  };

  const hasActiveFilters = useMemo(() => {
    return !!location || !!nationality || !!gender || !!verificationStatus || Object.keys(networkFilters).length > 0;
  }, [location, nationality, gender, verificationStatus, networkFilters]);

  const clearFilters = () => {
    setLocation("");
    setNationality("");
    setGender("");
    setVerificationStatus("");
    setNetworkFilters({});
    setCompareData([]);
  };

  const [finalEntityId, setFinalEntityId] = useState<number>(Number(localStorage.getItem("entityId")));

  useEffect(() => {
    const finalEntityId = Number(entityId || userId || localStorage.getItem("entityId"));
    setFinalEntityId(finalEntityId);
  }, [entityId, userId]);

  const { networkDataResults, isNetworkDataLoading, isNetworkDataHasError } = useNetworkList({
    tabValue,
    roleTab,
    finalEntityId,
    userParamId,
    location,
    nationality,
    gender,
    verificationStatus,
    roleEntitySelected,
    networkFilters,
    page,
  });

  const networkData = networkDataResults?.items || [];
  const paginationData = networkDataResults?.meta || {
    currentPage: 0,
    itemCount: 0,
    itemsPerPage: 0,
    totalItems: 0,
    totalPages: 0,
  };

  const checkCompareSelection = (value: any) => {
    let selectedData = [...compareData];
    if (!selectedData.find((el: any) => el.id === value?.id)) {
      selectedData.push({ id: value?.id, role: value?.role });
    } else {
      selectedData = selectedData.filter((el: any) => el.id !== value?.id);
    }
    setCompareData(selectedData);
  };

  const canCompare = (): boolean => {
    let isComparable: boolean = true;
    setTooltipMessage(t("components.compare.compareWarning"));

    if (compareData.length < 2) {
      setTooltipMessage(t("components.compare.compareWarning"));
      isComparable = false;
      return false;
    }

    let role: string = compareData[0].role;

    compareData.forEach((el: any) => {
      if (el.role !== role) {
        isComparable = false;
        setTooltipMessage(t("components.compare.compareWarningPersonas"));
        return;
      }
    });

    return isComparable;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "22px",
        width: "100%",
        height: "fit-content",
        minHeight: "866px",
      }}
    >
      <Typography variant="h4ExtraBold">{t("components.networkController.network")}</Typography>
      <NetworkTabs tabValue={tabValue} handleChangeTabValue={handleChangeTabValue} isEntity={isEntity} />
      {tabValue === 3 && isOwnNetworkTab ? (
        <MembersComingSoon />
      ) : (
        <>
          <NetworkRoles roleTab={roleTab} handleChangeChildTabValue={handleChangeChildTabValue} />
          {isOwnNetworkTab && roleTab !== 6 && (
            <NetworkFilters
              isNetworkDataLoading={isNetworkDataLoading}
              setLocation={setLocation}
              nationality={nationality}
              setNationality={setNationality}
              gender={gender}
              setGender={setGender}
              verificationStatus={verificationStatus}
              setVerificationStatus={setVerificationStatus}
              hasActiveFilters={hasActiveFilters}
              roleTab={roleTab}
              setShowModal={setShowModal}
              setAdvancedFilters={setAdvancedFilters}
              showModal={showModal}
              roleEntitySelected={roleEntitySelected}
              networkFilters={networkFilters}
              setNetworkFilters={setNetworkFilters}
              advancedFilters={advancedFilters}
              clearFilters={clearFilters}
              location={location}
            />
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body13Bold" sx={{ fontWeight: 500, color: colors.Black100 }}>
              {paginationData.totalItems}
              {tabValue === 0
                ? ` ${t("components.networkController.followers")}`
                : tabValue === 1
                ? ` ${t("components.networkController.followings")}`
                : ` ${t("components.networkController.friends")}`}
            </Typography>

            {roleTab !== 6 && (
              <>
                <Button
                  variant="text-sm"
                  onClick={() => {
                    const isComparable = canCompare();
                    if (isComparable) {
                      setShowCompareModal(true);
                    } else {
                      setToolTipShowMsg(true);
                      setTimeout(() => {
                        setToolTipShowMsg(false);
                      }, 1000);
                    }
                  }}
                  endIcon={
                    <Tooltip open={toolTipShowMsg} title={t("components.networkController.compareWarning")} arrow>
                      <InfoRoundedIcon />
                    </Tooltip>
                  }
                  sx={{
                    padding: "unset",
                    fontWeight: 400,
                    textDecoration: "underline",
                    "&:hover": {
                      background: "transparent",
                    },
                  }}
                >
                  {t("components.networkController.compare")}
                </Button>
                <CompareModal
                  openModal={showCompareModal}
                  compareData={compareData}
                  setCompareData={setCompareData}
                  closeModal={() => setShowCompareModal(false)}
                />
              </>
            )}
          </Box>

          <NetworkListing
            isNetworkDataLoading={isNetworkDataLoading}
            networkData={networkData}
            hasActiveFilters={hasActiveFilters}
            compareData={compareData}
            checkCompareSelection={checkCompareSelection}
          />
          {!isNetworkDataLoading && networkData.length !== 0 && (
            <Pagination
              count={Math.ceil(paginationData?.totalItems / paginationData?.itemsPerPage || 1)}
              page={!paginationData?.totalItems || paginationData?.totalItems <= 0 ? 1 : paginationData.currentPage + 1}
              onChange={handleChangePage}
              sx={{
                margin: "33px auto 0",
                direction: "ltr !important",
                "& .MuiButtonBase-root": {
                  borderRadius: "8px !important",
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default NetworkController;
